import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  JSX
} from 'react';
import { Area, CartesianGrid, ComposedChart, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { abbreviateNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { MdPushPin } from 'react-icons/md';
import './ChannelMix.scss';
import cn from 'classnames';
import { abbreviateScenarioValue } from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { SimpleLegend } from '@Components/Charts/Legend/SimpleLegend';
import { PercentChange } from '@Components/PercentChange/PercentChange';
import { Size, StretchMode } from '@Components/designConstants';
import { Neutral900 } from '@src/utils/colors';
import { AxisColor, GridColor, PinnedSpendLineColor } from '../utils/colors';
import { PinnedSpendPointData, ChannelMixDataPoint } from '../../hroas-types';
import {
  createChannelColorMap,
  getPercentageDifferenceIndicator
} from '../utils/efficientFrontierUtils';
import { formatPercentRounded } from '../../utils/hroasUtils';

interface ChannelMixProps {
  businessAsUsual: PinnedSpendPointData | undefined;
  chartData: ChannelMixDataPoint[];
  channelGroups: string[];
  viewType: 'absolute' | 'relative';
  pinnedSpendPoints: Map<number, PinnedSpendPointData>;
  setPinnedSpendPoints: Dispatch<SetStateAction<Map<number, PinnedSpendPointData>>>;
  marketingOutcomesChartData: PinnedSpendPointData[];
}

export const ChannelMix = ({
  businessAsUsual,
  chartData,
  channelGroups,
  viewType,
  pinnedSpendPoints,
  setPinnedSpendPoints,
  marketingOutcomesChartData
}: ChannelMixProps): JSX.Element => {
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const isRelative = viewType === 'relative';

  // The tooltip is portaled to document.body (below) so it can escape the card's `overflow: hidden`
  // clip and float over everything. We measure the chart's on-screen box to translate recharts'
  // chart-relative `coordinate` into viewport coordinates for the portaled tooltip.
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const displayData = useMemo(() => {
    if (!isRelative) {
      return chartData;
    }

    return chartData.map(dataPoint => {
      const newDataPoint: ChannelMixDataPoint = { totalBudget: dataPoint.totalBudget };

      let totalSpend = 0;
      channelGroups.forEach(group => {
        totalSpend += (dataPoint[group] as number) || 0;
      });

      channelGroups.forEach(group => {
        const spend = (dataPoint[group] as number) || 0;
        newDataPoint[group] = totalSpend > 0 ? (spend / totalSpend) * 100 : 0;
      });

      return newDataPoint;
    });
  }, [chartData, channelGroups, isRelative]);

  const channelColorMap = useMemo(() => createChannelColorMap(channelGroups), [channelGroups]);

  const legendItems = useMemo(
    () => channelGroups.map(group => ({ label: group, value: group })),
    [channelGroups]
  );

  const visibleChannelGroups = useMemo(() => {
    if (activeChannel === null) {
      return channelGroups;
    }
    return [activeChannel];
  }, [activeChannel, channelGroups]);

  const bauDataPoint = useMemo(() => {
    if (!businessAsUsual) {
      return null;
    }
    return displayData.find(d => d.totalBudget === businessAsUsual.totalBudget) || null;
  }, [displayData, businessAsUsual]);

  const xDomainTicks = useMemo(() => {
    if (displayData.length === 0) {
      return [];
    }
    const budgets = displayData.map(d => d.totalBudget);
    const xMin = Math.min(...budgets);
    const xMax = Math.max(...budgets);
    const tickCount = 12;
    if (xMax === xMin) {
      return [xMin];
    }
    const step = (xMax - xMin) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => xMin + step * i);
  }, [displayData]);

  return (
    <div className='ef-channel-mix-container'>
      {displayData.length > 0 && (
        <>
          <div className='cm-chart' ref={chartContainerRef}>
            <AutoSizer
              renderProp={({ width, height }) => (
                <ComposedChart
                  data={displayData}
                  width={width ?? 0}
                  height={height ?? 0}
                  style={{ overflow: 'visible' }}
                  onClick={(data: any) => {
                    if (data && data.activeLabel !== undefined) {
                      const budgetToPin = data.activeLabel as number;
                      const marketingOutcomesPoint = marketingOutcomesChartData.find(
                        point => point.totalBudget === budgetToPin
                      );

                      if (marketingOutcomesPoint) {
                        setPinnedSpendPoints(prev => {
                          const newMap = new Map(prev);
                          if (newMap.has(budgetToPin)) {
                            newMap.delete(budgetToPin);
                          } else {
                            newMap.set(budgetToPin, marketingOutcomesPoint);
                          }
                          return newMap;
                        });
                      }
                    }
                  }}
                  margin={{ top: 16, right: 24, bottom: 8 }}
                >
                  <XAxis
                    dataKey='totalBudget'
                    type='number'
                    domain={[xDomainTicks[0], xDomainTicks[xDomainTicks.length - 1]]}
                    ticks={xDomainTicks}
                    tickFormatter={value => `$${abbreviateNumber(value, 1)}`}
                    axisLine={{ stroke: GridColor }}
                    tick={{ fontSize: 12, fontWeight: 400, fill: AxisColor }}
                  />
                  <YAxis
                    yAxisId='left'
                    allowDecimals={false}
                    tickFormatter={
                      isRelative
                        ? value => formatPercentRounded(value)
                        : value => `$${abbreviateNumber(value, 1)}`
                    }
                    type='number'
                    domain={isRelative ? [0, 100] : [0, 'auto']}
                    ticks={isRelative ? [0, 25, 50, 75, 100] : undefined}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fontWeight: 400, fill: AxisColor }}
                  />
                  <Tooltip
                    portal={document.body}
                    // eslint-disable-next-line react/no-unstable-nested-components -- recharts render prop closes over parent state/props (businessAsUsual, bauDataPoint, channelColorMap, pinnedSpendPoints, isRelative); hoisting would require threading all of them through
                    content={({ active, payload, label, coordinate }) => {
                      if (active && payload && payload.length) {
                        const spend = label;
                        const isBau = Number(spend) === businessAsUsual?.totalBudget;
                        const spendDiff = getPercentageDifferenceIndicator(
                          Number(spend),
                          businessAsUsual?.totalBudget || 0,
                          isBau
                        );
                        return (
                          <ChannelMixTooltip
                            anchorRect={chartContainerRef.current?.getBoundingClientRect()}
                            coordinate={coordinate}
                          >
                            <div className='cm-tooltip-container'>
                              <div className='cm-tooltip'>
                                <span className='cm-tooltip-value'>
                                  {abbreviateScenarioValue(Number(spend), { monetary: true })}
                                </span>
                                <span className='cm-tooltip-label'>Total Spend</span>
                                {isBau && <span className='ef-bau-indicator'>BAU</span>}
                                {!isBau && spendDiff.percentChange !== null && (
                                  <PercentChange
                                    stretchMode={StretchMode.FIT}
                                    percentChange={spendDiff.percentChange}
                                    size={Size.SM}
                                  />
                                )}
                              </div>
                              <div className='cm-tooltip-breakout'>
                                {[...payload]
                                  .sort((a, b) => Number(b.value) - Number(a.value))
                                  .map(entry => {
                                    const name = String(entry.name ?? '');
                                    const entryValue = Number(entry.value);
                                    const channelBauValue = Number(bauDataPoint?.[name] ?? 0);
                                    const channelDiff = getPercentageDifferenceIndicator(
                                      entryValue,
                                      channelBauValue,
                                      isBau
                                    );

                                    return (
                                      <div
                                        key={String(entry.dataKey)}
                                        className='cm-tooltip-breakout-item'
                                      >
                                        <div className='cm-tooltip-breakout-item-label-wrapper'>
                                          <div
                                            className={cn('legend-icon', 'simple-icon', {
                                              dash: false
                                            })}
                                            style={{
                                              backgroundColor:
                                                channelColorMap[name] || entry.color || Neutral900
                                            }}
                                          />
                                          <span className='cm-tooltip-breakout-item-label'>
                                            {name}
                                          </span>
                                        </div>
                                        <span className='cm-tooltip-breakout-item-value'>
                                          {isRelative
                                            ? formatPercentRounded(entryValue)
                                            : abbreviateScenarioValue(entryValue, {
                                                monetary: true
                                              })}
                                        </span>
                                        <span className='cm-tooltip-breakout-item-delta'>
                                          {!isBau && channelDiff.indicatorText}
                                        </span>
                                      </div>
                                    );
                                  })}
                              </div>
                              {!isBau && <div className='cm-tooltip-delta-label'>%Δ vs BAU</div>}
                              <div className='cm-tooltip-footer'>
                                <MdPushPin />
                                <span className='cm-tooltip-footer-label'>
                                  {pinnedSpendPoints.has(Number(spend))
                                    ? 'Click to un-pin spend level'
                                    : 'Click to pin spend level'}
                                </span>
                              </div>
                            </div>
                          </ChannelMixTooltip>
                        );
                      }
                      return null;
                    }}
                  />
                  {Array.from(pinnedSpendPoints.keys()).map(budget => {
                    const isBau = budget === businessAsUsual?.totalBudget;
                    const lineColor = isBau ? AxisColor : PinnedSpendLineColor;
                    return (
                      <ReferenceLine
                        key={budget}
                        x={budget}
                        stroke={lineColor}
                        strokeDasharray='5 5'
                        strokeWidth={1}
                        yAxisId='left'
                        ifOverflow='extendDomain'
                        label={{
                          value: `$${(budget / 1000000).toFixed(1)}M`,
                          position: 'top',
                          fill: lineColor,
                          fontSize: 12,
                          fontWeight: 400
                        }}
                      />
                    );
                  })}
                  {visibleChannelGroups.map(group => (
                    <Area
                      key={group}
                      type='monotone'
                      dataKey={group}
                      stackId='1'
                      stroke={channelColorMap[group]}
                      fill={channelColorMap[group]}
                      name={group}
                      yAxisId='left'
                      isAnimationActive={false}
                    />
                  ))}
                  <CartesianGrid stroke={GridColor} strokeDasharray='0' vertical={false} />
                </ComposedChart>
              )}
            />
          </div>
          <div className='cm-legend'>
            <SimpleLegend
              legendItems={legendItems}
              activeItem={activeChannel}
              handleLegendClick={channel =>
                setActiveChannel(prev => (prev === channel ? null : channel))
              }
              setHoveredLegend={setHoveredLegend}
              handleLegendEnter={channel =>
                setHoveredLegend(prev => (prev === channel ? null : channel))
              }
              hoveredLegend={hoveredLegend}
              colorMap={channelColorMap}
            />
          </div>
        </>
      )}
    </div>
  );
};

interface ChannelMixTooltipProps {
  // Chart's on-screen box (viewport coordinates); recharts' `coordinate` is relative to its top-left.
  anchorRect: DOMRect | undefined;
  coordinate: { x?: number; y?: number } | undefined;
  children: ReactNode;
}

// Positions the portaled tooltip at the cursor in viewport coordinates, clamped to stay fully on
// screen. Rendered into document.body (via `<Tooltip portal>`), so it escapes the card's
// `overflow: hidden` clip and floats above everything regardless of its height (dozens of channels).
const ChannelMixTooltip = ({ anchorRect, coordinate, children }: ChannelMixTooltipProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!ref.current || !anchorRect || !coordinate) {
      return;
    }
    const { width, height } = ref.current.getBoundingClientRect();
    const margin = 12;
    const anchorX = anchorRect.left + (coordinate.x ?? 0);
    const anchorY = anchorRect.top + (coordinate.y ?? 0);

    // Prefer opening to the right of the cursor; flip left if it would overflow the viewport.
    let left = anchorX + margin;
    if (left + width > window.innerWidth - margin) {
      left = anchorX - width - margin;
    }
    left = Math.max(margin, left);

    // Anchor near the cursor vertically, then clamp so the whole tooltip stays on screen.
    let top = anchorY + margin;
    top = Math.min(top, window.innerHeight - height - margin);
    top = Math.max(margin, top);

    setPosition({ left, top });
  }, [anchorRect, coordinate]);

  return (
    <div
      ref={ref}
      className='cm-tooltip-portal'
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? 'visible' : 'hidden'
      }}
    >
      {children}
    </div>
  );
};
